import React, { useEffect, useState, createContext, useContext, ReactNode, useCallback, useMemo, useRef } from "react";
import { HumanBehaviorTracker, logError, logWarn, logDebug } from "./browser";

// Check if we're in a browser environment
const isBrowser = () => typeof window !== 'undefined';

// Define the public interface that components will interact with
interface HumanBehaviorInterface {
  addEvent: (event: any) => void;
  identifyUser: ({ userProperties }: { userProperties: Record<string, any> }) => Promise<string>;
  start: () => void;
  stop: () => void;
  logout: () => void;
  viewLogs: () => void;
  setUnredactedFields: (fields: string[]) => void;
  hasUnredactedFields: () => boolean;
  getUnredactedFields: () => string[];
  trackPageView: () => void;
}

interface HumanBehaviorContextType {
  humanBehavior: HumanBehaviorTracker | null;
  queueEvent: (event: any) => void;
}

interface HumanBehaviorProviderProps {
  // Either provide an apiKey to create a new client, or provide an existing client
  apiKey?: string;
  client?: HumanBehaviorTracker;
  children: ReactNode;
  options?: {
    ingestionUrl?: string;
    logLevel?: 'none' | 'error' | 'warn' | 'info' | 'debug';
    redactFields?: string[]; // DEPRECATED: Use redactionStrategy instead
    redactionStrategy?: {
      mode: 'privacy-first' | 'visibility-first'; // Default: 'privacy-first'
      unredactFields?: string[]; // Fields to make visible (when mode: 'privacy-first')
      redactFields?: string[];   // Fields to hide (when mode: 'visibility-first')
    };
    enableAutomaticTracking?: boolean;
    suppressConsoleErrors?: boolean;
    recordCanvas?: boolean; // Enable canvas recording with protection
    enableAutomaticProperties?: boolean; // Enable automatic property detection
    propertyDenylist?: string[]; // Properties to exclude from tracking
    automaticTrackingOptions?: {
      trackButtons?: boolean;
      trackLinks?: boolean;
      trackForms?: boolean;
      includeText?: boolean;
      includeClasses?: boolean;
    };
    maxQueueSize?: number; // Configurable queue size
    enableConsoleTracking?: boolean; // Enable console warn/error tracking (default: true, set to false to opt-out)
    enableNetworkTracking?: boolean; // Enable network error tracking (default: true, set to false to opt-out)
  };
}

// Default context to prevent unnecessary re-renders
const defaultContext: HumanBehaviorContextType = {
  humanBehavior: null,
  queueEvent: (event: any) => {
    // In server-side, just no-op
    if (typeof window === 'undefined') {
      return;
    }
    logWarn('HumanBehavior not initialized yet, event queued:', event);
  }
};

const HumanBehaviorContext = createContext<HumanBehaviorTracker | HumanBehaviorInterface | null>(null);

export const HumanBehaviorProvider = ({ apiKey, client, children, options }: HumanBehaviorProviderProps) => {
  const [humanBehavior, setHumanBehavior] = useState<HumanBehaviorTracker | null>(client || null);
  const [eventQueue, setEventQueue] = useState<any[]>([]);
  const [isMounted, setIsMounted] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Use refs to avoid dependency issues in useEffect
  const apiKeyRef = useRef(apiKey);
  const clientRef = useRef(client);
  const eventQueueRef = useRef(eventQueue);
  
  // Update refs when props change
  useEffect(() => {
    apiKeyRef.current = apiKey;
    clientRef.current = client;
  }, [apiKey, client]);
  
  // Update eventQueue ref when queue changes
  useEffect(() => {
    eventQueueRef.current = eventQueue;
  }, [eventQueue]);

  // Memoized queueEvent function to prevent unnecessary re-renders
  const queueEvent = useCallback((event: any) => {
    setEventQueue(prev => [...prev, event]);
  }, []);

  // Handle mounting state
  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    // Only run in browser
    if (!(isBrowser())) {
      return;
    }

    // If client is provided, use that
    if (clientRef.current) {
      setHumanBehavior(clientRef.current);
      setIsInitialized(true);
      return;
    }

    // If no client is provided, apiKey is required
    if (!apiKeyRef.current || apiKeyRef.current.trim() === '') {
      logError("An apiKey is required when no client is provided");
      return;
    }

    // Prevent multiple initializations
    if (humanBehavior !== null) {
      return;
    }

    try {
      
      // Create new tracker instance with the validated apiKey and options
      const tracker = HumanBehaviorTracker.init(
        apiKeyRef.current.trim(),
        {
          ingestionUrl: options?.ingestionUrl,
          logLevel: options?.logLevel,
          redactFields: options?.redactFields, // Legacy support
          redactionStrategy: options?.redactionStrategy, // New redaction strategy
          enableAutomaticTracking: options?.enableAutomaticTracking,
          suppressConsoleErrors: options?.suppressConsoleErrors,
          recordCanvas: options?.recordCanvas,
          enableAutomaticProperties: options?.enableAutomaticProperties,
          propertyDenylist: options?.propertyDenylist,
          automaticTrackingOptions: options?.automaticTrackingOptions,
          maxQueueSize: options?.maxQueueSize,
          enableConsoleTracking: options?.enableConsoleTracking,
          enableNetworkTracking: options?.enableNetworkTracking,
        }
      );
      
      setHumanBehavior(tracker);

      // Mark as initialized immediately (don't wait for server init)
      setIsInitialized(true);
      
      // Start tracking in background
      tracker.initializationPromise?.then(async () => {
          // Only start if not already started
          if (!tracker.isTrackerStarted) {
              await tracker.start();
          }
          
        // Process any queued events
        const currentQueue = eventQueueRef.current;
        if (currentQueue.length > 0) {
          for (const event of currentQueue) {
            if (event.type === 'identify') {
              logDebug('Processing queued identify event', event);
              try {
                await tracker.identifyUser({ userProperties: event.userProperties });
              } catch (error) {
                logError('Failed to process queued user info:', error);
              }
            } else {
              tracker.addEvent(event);
            }
          }
          setEventQueue([]); // Clear the queue
        }
      }).catch(error => {
        logError('Failed to initialize HumanBehaviorTracker:', error);
      });
    } catch (error) {
      logError('Failed to create HumanBehaviorTracker:', error);
    }
  }, [isMounted]); // Removed humanBehavior from dependencies to prevent infinite loop

  // Memoized context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => {
    if (!isMounted) {
      return createQueuingImplementation(queueEvent);
    }
    
    if (!isInitialized) {
      return createQueuingImplementation(queueEvent);
    }
    
    return humanBehavior;
  }, [isMounted, isInitialized, humanBehavior, queueEvent]);

  // If not in browser, render children without context
  if (!(isBrowser())) {
    return <>{children}</>;
  }

  return (
    <HumanBehaviorContext.Provider value={contextValue}>
      <HumanBehaviorPageView />
      {children}
    </HumanBehaviorContext.Provider>
  );
};

// Default implementation for when tracker is not available
const defaultImplementation: HumanBehaviorInterface = {
  addEvent: () => {},
  identifyUser: async ({ userProperties }: { userProperties: Record<string, any> }) => {
    return '';
  },
  start: () => {},
  stop: () => {},
  logout: () => {},
  viewLogs: () => {},
  setUnredactedFields: () => {},
  hasUnredactedFields: () => false,
  getUnredactedFields: () => [],
  trackPageView: () => {},
};

// Memoized queuing implementation for initialization period
const createQueuingImplementation = (queueEvent: (event: any) => void): HumanBehaviorInterface => ({
  addEvent: (event: any) => {
    queueEvent(event);
  },
  identifyUser: async ({ userProperties }: { userProperties: Record<string, any> }) => {
    queueEvent({
      type: 'identify',
      userProperties,
    });
    return ''; // Return empty string to match interface
  },
  start: () => {
    // Start will be called automatically when initialized
  },
  stop: () => {
    // Stop is a no-op when not initialized
  },
  logout: () => {
    // Logout is a no-op when not initialized
  },
  viewLogs: () => {
    logWarn('Logs are not available until HumanBehaviorTracker is initialized');
  },
  setUnredactedFields: () => {},
  hasUnredactedFields: () => false,
  getUnredactedFields: () => [],
  trackPageView: () => {},
});

export const useHumanBehavior = () => {
  const tracker = useContext(HumanBehaviorContext);
  if (!tracker) {
    throw new Error('useHumanBehavior must be used within a HumanBehaviorProvider');
  }
  return tracker;
};

// Custom hook for managing unredaction fields dynamically
export const useRedaction = () => {
  const tracker = useHumanBehavior();
  
  const setUnredactedFields = useCallback((fields: string[]) => {
    tracker.setUnredactedFields(fields);
  }, [tracker]);
  
  const hasUnredactedFields = useCallback(() => {
    return tracker.hasUnredactedFields();
  }, [tracker]);
  
  const getUnredactedFields = useCallback(() => {
    return tracker.getUnredactedFields();
  }, [tracker]);
  
  return {
    setUnredactedFields,
    hasUnredactedFields,
    getUnredactedFields
  };
};

// Custom hook for managing user info
export const useUserTracking = () => {
  const tracker = useHumanBehavior();
  
  const identifyUser = useCallback(async ({ userProperties }: { userProperties: Record<string, any> }) => {
    try {
      await tracker.identifyUser({ userProperties });
      return { success: true };
    } catch (error) {
      logError('Failed to identify user:', error);
      return { success: false, error };
    }
  }, [tracker]);
  
  return {
    identifyUser
  };
};

// Automatic page tracking component
function HumanBehaviorPageView() {
  const tracker = useContext(HumanBehaviorContext);

  useEffect(() => {
    if (tracker && typeof window !== 'undefined') {
      // Track initial page load
      tracker.trackPageView();

      // Listen for route changes (for SPAs)
      const handleRouteChange = () => {
        tracker.trackPageView();
      };

      // Listen for popstate (back/forward navigation)
      window.addEventListener('popstate', handleRouteChange);

      // Listen for pushstate/replacestate (programmatic navigation)
      const originalPushState = history.pushState;
      const originalReplaceState = history.replaceState;

      history.pushState = function(...args) {
        originalPushState.apply(this, args);
        handleRouteChange();
      };

      history.replaceState = function(...args) {
        originalReplaceState.apply(this, args);
        handleRouteChange();
      };

      return () => {
        window.removeEventListener('popstate', handleRouteChange);
        history.pushState = originalPushState;
        history.replaceState = originalReplaceState;
      };
    }
  }, [tracker]);

  return null;
}

// Export the tracker class for direct use
export { HumanBehaviorTracker };
